import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Activity, Scale, Calculator as CalcIcon, MessagesSquare, X } from "lucide-react";

/**
 * Floating quick-action button. Fixed bottom-right on all authenticated
 * pages, hidden on the pages it links to (avoids redundancy) and on the
 * public /login / /maintenance screens (mounted from AppLayout so gated).
 * A tap opens a small radial menu; tap an action to navigate.
 */
export default function FloatingLogButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);

  const go = (route) => { setOpen(false); navigate(route); };

  const actions = [
    { icon: Activity, label: "Log Shot", route: "/dashboard?logshot=1", brand: true },
    { icon: Scale, label: "Log Weight", route: "/weight" },
    { icon: CalcIcon, label: "Reconstitute", route: "/calculators" },
    { icon: MessagesSquare, label: "New Topic", route: "/community" },
  ];

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="fab-overlay"
        />
      )}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3" data-testid="fab-container">
        {open && (
          <div className="flex flex-col items-end gap-2" data-testid="fab-menu">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => go(a.route)}
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-md border font-mono-num text-[11px] uppercase tracking-widest transition-colors shadow-sm ${
                  a.brand
                    ? "bg-[#0D9488] text-white border-[#0D9488] hover:bg-[#0F766E]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:border-[#2DD4BF] dark:hover:text-[#2DD4BF]"
                }`}
                data-testid={`fab-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <a.icon className="w-3.5 h-3.5" />
                {a.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-14 h-14 bg-[#0D9488] hover:bg-[#0F766E] text-white flex items-center justify-center shadow-xl transition-transform hover:scale-105"
          style={{ boxShadow: "0 12px 32px -8px rgba(13,148,136,0.55), 0 0 0 1px rgba(15,23,42,0.15)" }}
          aria-label={open ? "Close quick actions" : "Open quick actions"}
          data-testid="fab-trigger"
        >
          {open ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" strokeWidth={2.5} />}
        </button>
      </div>
    </>
  );
}
